import { UrgencyRating } from '@dermloop/static-values';
import { isGroupField, isWidgetField } from '../model/form-field';
import { isDiagnosisWidget } from '../model/form-template-document.types';
import type {
  GroupField,
  WidgetField,
} from '../model/form-template-document.types';

/**
 * Publish-time registries for the open strings the meta-schema leaves untyped
 * (ref-data keys, image-type keys, location-picker asset keys). A new entry is a
 * data change, never a language change.
 */

/** ref-data option-list keys — the launch set. */
export const FORM_REF_DATA_KEYS = [
  'symptoms',
  'general-symptoms',
  'appearance-changes',
  'approximate-ages',
  'ambivalent-factors',
  'treatments',
  'biopsy-types',
  'diagnostic-difficulties',
  'lesion-thickness',
] as const;

/** image-capture image-type keys — dermatology, muco, and cervix photos. */
export const FORM_IMAGE_TYPE_KEYS = [
  'dermoscopic',
  // `overview` frames a single lesion and carries its location marker;
  // `overview-rash` frames a spread-out condition, which has no single spot to
  // mark. Same framing guidance and detectors otherwise.
  'overview',
  'overview-rash',
  'close-up',
  'muco-lips-closed',
  'muco-occlusion-gums',
  'muco-tongue-dorsum',
  'muco-tongue-lateral-left',
  'muco-tongue-lateral-right',
  'muco-tongue-ventral',
  'muco-mouth-floor',
  'muco-buccal-left',
  'muco-buccal-right',
  'muco-palate',
  'muco-lesion',
  'gyn-cervix',
] as const;

/** An image-capture image-type key from the publish-time registry. */
export type FormImageTypeKey = (typeof FORM_IMAGE_TYPE_KEYS)[number];

/**
 * location-picker asset keys — the surface each picker renders on. Used to tell
 * apart multiple same-dimensionality pickers in one form (e.g. the 2D mouth
 * diagrams).
 */
export const FORM_ASSET_KEYS = [
  'muco-mouth-upper',
  'muco-mouth-floor',
] as const;

/**
 * Reads one of these open string keys off a platform's registry of what it can
 * actually render. `hasOwn`, because the key is validated only at publish time:
 * a plain lookup of `constructor` or `toString` resolves to an inherited member,
 * which the caller would then use as if it were a registered entry.
 */
export function resolveOpenKey<TEntry>(
  registry: Partial<Record<string, TEntry>>,
  key: string | undefined,
): TEntry | undefined {
  return key && Object.hasOwn(registry, key) ? registry[key] : undefined;
}

/** Option values are kebab-case, so `sub_acute` cannot be authored verbatim. */
export const URGENCY_OPTION_VALUES: Partial<Record<string, UrgencyRating>> = {
  routine: UrgencyRating.Routine,
  'sub-acute': UrgencyRating.SubAcute,
  acute: UrgencyRating.Acute,
};

export interface FormRoleDefinition {
  repeatable: boolean;
  description: string;
  accepts: (field: WidgetField | GroupField, isConcept: boolean) => boolean;
}

/**
 * Both diagnosis roles target the same widgets, so readers resolve primary and
 * differential by role whichever picker a template authors.
 */
function isRoleBearingDiagnosisWidget(
  field: WidgetField | GroupField,
): boolean {
  return (
    isWidgetField(field) &&
    isDiagnosisWidget(field) &&
    !!field.bind?.['selected']
  );
}

/** Roles declare meaning independently of the paths holding the answers. */
export const FORM_ROLES: Readonly<Record<string, FormRoleDefinition>> = {
  'follow-up-months': {
    repeatable: false,
    description: 'a single-select or scale widget with a selected bind',
    accepts: (field) =>
      isWidgetField(field) &&
      (field.widget === 'single-select' || field.widget === 'scale') &&
      !!field.bind?.['selected'],
  },
  'topical-treatment': {
    repeatable: false,
    description: 'a text or note widget with a value bind',
    accepts: (field) =>
      isWidgetField(field) &&
      (field.widget === 'text' || field.widget === 'note') &&
      !!field.bind?.['value'],
  },
  urgency: {
    repeatable: false,
    description:
      'a scale widget with a selected bind whose options are routine, sub-acute or acute',
    accepts: (field) =>
      isWidgetField(field) &&
      field.widget === 'scale' &&
      !!field.bind?.['selected'] &&
      field.options.items.every(
        (option) => !!resolveOpenKey(URGENCY_OPTION_VALUES, option.value),
      ),
  },
  'management-action': {
    repeatable: false,
    description: 'a clinical-action widget with a selected bind',
    accepts: (field) =>
      isWidgetField(field) &&
      field.widget === 'clinical-action' &&
      !!field.bind?.['selected'],
  },
  'medical-record-note': {
    repeatable: false,
    description: 'a note or text widget with a value bind',
    accepts: (field) =>
      isWidgetField(field) &&
      (field.widget === 'note' || field.widget === 'text') &&
      !!field.bind?.['value'],
  },
  'time-spent': {
    repeatable: false,
    description: 'a number widget with a value bind',
    accepts: (field) =>
      isWidgetField(field) &&
      field.widget === 'number' &&
      !!field.bind?.['value'],
  },
  'primary-diagnosis': {
    repeatable: false,
    description:
      'a diagnosis-tree or diagnosis-search widget with a selected bind',
    accepts: isRoleBearingDiagnosisWidget,
  },
  'differential-diagnosis': {
    repeatable: true,
    description:
      'a diagnosis-tree or diagnosis-search widget with a selected bind',
    accepts: isRoleBearingDiagnosisWidget,
  },
  'image-quality': {
    repeatable: true,
    description:
      'a top-level field or group holding exactly one scale with a selected bind',
    accepts: (field, isConcept) => {
      const widgets = isGroupField(field) ? field.group.fields : [field];
      const scales = widgets.filter((widget) => widget.widget === 'scale');
      return isConcept && scales.length === 1 && !!scales[0].bind?.['selected'];
    },
  },
};
